import React, { useState, useMemo } from 'react';
import MaterialTable from 'material-table';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import theme from '../../styles/tableStyle';
import TableStyle from './Style';
import localization from './Localization';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY, SECONDORY_COLOR } from '../../common/sharedFunctions';

const PAGE_SIZE = 20;

export default function ResponsiveTable({
  columns,
  data,
  title,
  actions,
  options,
  renderCard,
  onRowClick,
  components,
  editable,
  ...rest
}) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const tableOptions = { ...TableStyle(), ...options };
  const tableLocalization = localization(t);

  const filteredData = useMemo(() => {
    if (!search.trim() || !data) return data || [];
    const term = search.toLowerCase();
    return data.filter(row => {
      return columns.some(col => {
        const value = col.field ? row[col.field] : null;
        if (value == null) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [data, search, columns]);

  if (!isMobile) {
    return (
      <ThemeProvider theme={theme}>
        <MaterialTable
          title={title}
          columns={columns}
          data={data || []}
          actions={actions}
          options={tableOptions}
          localization={tableLocalization}
          onRowClick={onRowClick}
          components={components}
          editable={editable}
          {...rest}
        />
      </ThemeProvider>
    );
  }

  // Mobile card view
  const visibleData = filteredData.slice(0, visibleCount);
  const hasMore = visibleCount < filteredData.length;

  return (
    <Box sx={{ px: 1, py: 1 }}>
      {title && (
        <Typography
          variant="h6"
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 'bold',
            mb: 1.5,
            px: 0.5,
          }}
        >
          {title}
        </Typography>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder={t('search')}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setVisibleCount(PAGE_SIZE);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#999' }} />
            </InputAdornment>
          ),
          sx: {
            fontFamily: FONT_FAMILY,
            borderRadius: '10px',
            backgroundColor: '#f5f5f5',
            height: 44,
          },
        }}
        sx={{ mb: 1.5 }}
      />

      {visibleData.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ color: '#999', fontFamily: FONT_FAMILY }}>
            {t('blank_message')}
          </Typography>
        </Box>
      ) : (
        <>
          {visibleData.map((row, index) => (
            <Box key={row.id || row.tableData?.id || index}>
              {renderCard ? (
                renderCard(row, index)
              ) : (
                <DefaultCard row={row} columns={columns} actions={actions} />
              )}
            </Box>
          ))}

          {hasMore && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
              sx={{
                mt: 1,
                mb: 2,
                fontFamily: FONT_FAMILY,
                borderRadius: '10px',
                height: 44,
                textTransform: 'none',
                borderColor: SECONDORY_COLOR,
                color: '#333',
              }}
            >
              {t('load_more') || 'Load more'} ({filteredData.length - visibleCount} {t('remaining') || 'remaining'})
            </Button>
          )}
        </>
      )}
    </Box>
  );
}

function DefaultCard({ row, columns }) {
  return (
    <Box
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: '12px',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid #eee',
      }}
    >
      {columns
        .filter(col => !col.hidden)
        .slice(0, 6)
        .map((col, i) => {
          const value = col.field ? row[col.field] : '';
          const rendered = col.render ? col.render(row) : value;
          return (
            <Box key={col.field || i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: 13, color: '#666', fontFamily: FONT_FAMILY }}>
                {col.title}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 500, fontFamily: FONT_FAMILY, textAlign: 'right', maxWidth: '60%' }}>
                {rendered != null ? rendered : '-'}
              </Typography>
            </Box>
          );
        })}
    </Box>
  );
}
